/**
 * What counts as "during the school day", for the calendar rail's filter.
 *
 * THE HOURS ARE A CONSTANT HERE, not a lookup. The school's real bell times do
 * reach the API — Edval publishes them over LISS — but `publish_bell_times` is
 * an unimplemented stub, so nothing is stored and there is nothing to read.
 * When that lands, this module is the one place that has to change.
 *
 * The wording lives beside the numbers for the same reason: the tooltip on the
 * rail quotes the window, and a label saying one thing while the test does
 * another is worse than no label.
 */

/** Minutes past local midnight, which is the unit every test below works in. */
const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
const MS_PER_MINUTE = 60_000;

/** 7:30am. */
export const SCHOOL_DAY_START = 7 * MINUTES_PER_HOUR + 30;
/** 4pm. */
export const SCHOOL_DAY_END = 16 * MINUTES_PER_HOUR;

/** The window as the rail says it out loud. Keep in step with the two above. */
export const SCHOOL_DAY_LABEL = "7:30am and 4pm";

const minutesOfDay = (d: Date): number => d.getHours() * 60 + d.getMinutes();

const sameLocalDay = (a: Date, b: Date): boolean =>
	a.getDate() === b.getDate() &&
	a.getMonth() === b.getMonth() &&
	a.getFullYear() === b.getFullYear();

/**
 * Whether any part of an event falls inside the school day.
 *
 * OVERLAP, not containment. An excursion that leaves at 7am and returns at 5pm
 * is a school-day event by any reading a person would give it, and so is an
 * all-day event; only something wholly outside the window — a 6am squad
 * session, a 7pm concert — is not. The edges are closed against the window
 * rather than open: an event ending exactly at 7:30 has not overlapped the
 * school day, and one starting exactly at 4pm is after it.
 *
 * LOCAL TIME, via the Date getters rather than any UTC part. The instants
 * arrive as ISO strings and the question is about a clock on a wall in Sydney.
 *
 * Three shapes have to be right, and they are why this is not a one-liner:
 *
 *  - A POINT. An event with no end, or an end at or before its start, is given
 *    a notional minute so that a 7:30am start is inside the window rather than
 *    failing an exclusive comparison against its own edge.
 *  - MIDNIGHT. Under 24 hours but ending on another date, the event covers the
 *    tail of one day and the head of another, and either piece can be the one
 *    that overlaps. Tested as two segments, which is why Monday 23:00 to
 *    Tuesday 02:00 is correctly outside while Monday 23:00 to Tuesday 09:00 is
 *    inside.
 *  - A FULL DAY OR MORE. Anything spanning 24 hours contains a whole window
 *    wherever it starts, so it short-circuits.
 *
 * UNJUDGEABLE EVENTS ARE SHOWN. A missing or unparseable start returns true:
 * this filter hides things, and hiding an event because its data is thin is
 * the one outcome nobody could explain from what is on screen.
 */
export function overlapsSchoolDay(
	startIso?: string,
	endIso?: string,
): boolean {
	if (!startIso) return true;
	const start = new Date(startIso);
	if (Number.isNaN(start.getTime())) return true;

	const parsedEnd = endIso ? new Date(endIso) : null;
	const end =
		parsedEnd && !Number.isNaN(parsedEnd.getTime()) && parsedEnd > start
			? parsedEnd
			: new Date(start.getTime() + MS_PER_MINUTE);

	if (end.getTime() - start.getTime() >= MINUTES_PER_DAY * MS_PER_MINUTE) {
		return true;
	}

	const from = minutesOfDay(start);
	const to = minutesOfDay(end);

	if (sameLocalDay(start, end)) {
		return from < SCHOOL_DAY_END && to > SCHOOL_DAY_START;
	}

	/* Two segments: [from, midnight) on the first date, [midnight, to] on the
	   last. The first overlaps if it starts before the window closes — it runs
	   to the end of the day, so nothing else can exclude it — and the second if
	   it runs past the window opening. */
	return from < SCHOOL_DAY_END || to > SCHOOL_DAY_START;
}
